import { useEffect, useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Clock, ChevronLeft, ChevronRight, Send, Navigation, Train, BookOpen, CalendarDays, IndianRupee, Briefcase, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ConsentCheckbox, { CONSENT_TEXT } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";

const TAWK_SRC = "https://embed.tawk.to/615ae174d326717cb6849dee/1fh5g1cc9";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/fce244d9-4f06-4792-baea-c2f10101634a/images/5691f1eb3d9bf07899c2d3e8ab5bb33d826705b7fe6f0988aa6aa4abda4fa5cc.jpeg";

const FALLBACK_OFFICES = [
  {
    tab: "Noida Sector 2 (Head Office)", name: "Head Office — Noida Sector 2",
    address: "B-25, Sector 2, Near Noida Sector 15 Metro Station, Noida, Uttar Pradesh 201301",
    metro: "Noida Sector 15 Metro Station",
    q: "Training Basket, B-25, Sector 2, Noida",
    photo: "https://images.unsplash.com/photo-1566447501963-c25eb7fa5bb9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    tab: "Noida Sector 62 (Training Centre)", name: "Training Centre — Noida Sector 62",
    address: "A-118, Sector 62, Near Electronic City Metro Station, Noida, Uttar Pradesh 201309",
    metro: "Noida Electronic City Metro Station",
    q: "Training Basket, A-118, Sector 62, Noida",
    photo: "https://images.unsplash.com/photo-1703745006226-98d4b948dc41?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
];

const TEAM_PHOTOS = [
  { src: "https://images.unsplash.com/photo-1576267423048-15c0040fec78?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Training Basket admissions team celebrating with students" },
  { src: "https://images.unsplash.com/photo-1781246212288-7fa538344718?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Training Basket counselors collaborating around a conference table" },
  { src: "https://images.unsplash.com/photo-1681164315051-add1906a9b07?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Training Basket team working on laptops in the office" },
  { src: "https://images.unsplash.com/photo-1737573477556-ac3ed2db618c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Training Basket support specialist at a laptop" },
];

const HERO_PILLS = [
  { icon: BookOpen, label: "Course Guidance" },
  { icon: CalendarDays, label: "Batch Schedule" },
  { icon: IndianRupee, label: "Fees & Offers" },
  { icon: Briefcase, label: "Placements Support" },
];

const text = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/&#x27;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();

export default function ContactPage({ lp }) {
  const raw = lp.body_html || lp.overview_html || "";
  const advIdx = raw.search(/<h2[^>]*>\s*Talk to our career advisors/i);
  const mapIdx = raw.search(/<h2[^>]*>\s*Map your Visit/i);
  const advisorsSeg = advIdx >= 0 ? raw.slice(advIdx, mapIdx > advIdx ? mapIdx : raw.length) : "";
  const mapSeg = mapIdx >= 0 ? raw.slice(mapIdx) : "";

  const advisorsTitle = text((advisorsSeg.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i) || [])[1]) || "Talk to our career advisors now";
  const advisorsSub = text((advisorsSeg.match(/<p[^>]*>([\s\S]*?)<\/p>/i) || [])[1]);
  const helplineBits = [...advisorsSeg.matchAll(/<h6[^>]*>([\s\S]*?)<\/h6>/gi)].map((m) => text(m[1]));
  const mapTitle = text((mapSeg.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i) || [])[1]) || "Map your Visit to Us";
  const mapSub = text((mapSeg.match(/<p[^>]*>([\s\S]*?)<\/p>/i) || [])[1]);
  const legacySectors = [...mapSeg.matchAll(/<h6[^>]*>([\s\S]*?)<\/h6>/gi)].map((m) => text(m[1])).filter(Boolean);

  const [office, setOffice] = useState(0);
  const [slide, setSlide] = useState(0);
  const [programs, setPrograms] = useState([]);
  const [centres, setCentres] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", course: "", mode: "Online", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [consent, setConsent] = useState(true);

  useEffect(() => {
    api.get("/programs", { params: { page_size: 50 } }).then((r) => setPrograms(r.data.programs || r.data || [])).catch(() => {});
    api.get("/website/sections/centres")
      .then((r) => setCentres(((r.data || {}).items || []).filter((c) => c && c.visible !== false && c.name)))
      .catch(() => setCentres([]));
  }, []);

  const openChat = () => {
    if (window.Tawk_API && window.Tawk_API.maximize) { window.Tawk_API.maximize(); return; }
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_API.onLoad = function () {
      if (window.innerWidth < 768 && window.Tawk_API.hideWidget) window.Tawk_API.hideWidget();
      window.Tawk_API.maximize();
    };
    const s = document.createElement("script");
    s.async = true; s.src = TAWK_SRC; s.charset = "UTF-8"; s.setAttribute("crossorigin", "*");
    document.body.appendChild(s);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!consent) { setErr("Please tick the consent box to continue."); return; }
    setSending(true); setErr("");
    try {
      await api.post("/inquiries", {
        name: form.name, email: form.email, phone: form.phone, message: form.message,
        program_slug: form.course || "", preferred_mode: form.mode,
        page: "/contact-us",
        attribution: { source: "landing_page_contact", path: "/contact-us", page: "/contact-us" },
        consent_given: consent, consent_text: CONSENT_TEXT,
      });
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Something went wrong. Please try again or call us directly.");
    } finally { setSending(false); }
  };

  const fld = "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FFC72C]/60 focus:border-[#FFC72C] transition";
  const lbl = "block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5";
  // Centres come from the same CMS section that drives the homepage — single source of truth.
  // Falls back to the legacy static list only if the CMS section is unavailable.
  const offices = centres.length
    ? centres.map((c) => ({
        tab: c.name,
        name: c.name,
        address: c.address || "",
        metro: (c.highlights || []).find((h) => /metro/i.test(String(h))) || "",
        q: (c.maps_url && decodeURIComponent((c.maps_url.match(/[?&]query=([^&]+)/) || [])[1] || "").replace(/\+/g, " ")) || c.address || c.name,
        photo: c.image || FALLBACK_OFFICES[0].photo,
      }))
    : FALLBACK_OFFICES;
  const cur = offices[Math.min(office, offices.length - 1)];

  return (
    <div className="bg-white text-slate-900" data-testid="contact-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />
              <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-500">Contact Us</span>
            </div>
            <h1 className="m-0 mt-4 text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.08] text-[#17263B]">
              Contact{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Training Basket</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FFC72C] rounded-sm" aria-hidden="true" />
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-500 max-w-xl">
              Have questions about our courses, batches, fees, admissions or placements? Our team is here to help you choose the right program and grow your career.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#contact" data-testid="contact-hero-cta"
                className="inline-flex items-center gap-2 min-h-[52px] px-7 bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.98] rounded-full font-extrabold text-[15px] text-[#17263B] shadow-[0_10px_24px_rgba(255,199,44,.35)] transition-all">
                Enrol now <ArrowRight className="w-[18px] h-[18px]" />
              </a>
              <a href="tel:+919015887887" data-testid="contact-hero-call"
                className="inline-flex items-center gap-2 min-h-[52px] px-6 bg-white border-[1.5px] border-slate-200 rounded-full font-bold text-[15px] text-[#17263B] hover:border-[#FFC72C] transition-colors">
                <Phone className="w-[18px] h-[18px] text-amber-600" /> +91 9015 887 887
              </a>
            </div>
          </div>
          <div className="relative min-w-0 max-w-md mx-auto w-full lg:max-w-none">
            <div className="absolute -top-8 -right-6 w-64 h-64 bg-[#FFC72C] rounded-full opacity-90 blur-2xl" aria-hidden="true" />
            <div className="relative rounded-[2.5rem] overflow-hidden border-4 border-[#FFC72C] shadow-[0_30px_60px_-20px_rgba(23,38,59,0.35)]">
              <img src={HERO_IMG} alt="Training Basket career counselor ready to help" className="w-full aspect-[4/5] object-cover" data-testid="contact-hero-image" />
            </div>
            {HERO_PILLS.map((p, i) => (
              <span key={p.label}
                className={`absolute flex items-center gap-1.5 bg-white rounded-full shadow-lg border border-slate-100 px-3.5 py-1.5 text-xs font-bold text-[#17263B] ${i % 2 ? "-right-2 sm:-right-4" : "-left-2 sm:-left-4"}`}
                style={{ top: `${12 + i * 22}%` }} data-testid={`contact-hero-pill-${i}`}>
                <p.icon className="w-3.5 h-3.5 text-amber-600" /> {p.label}
              </span>
            ))}
            <div className="absolute -bottom-4 left-6 bg-[#17263B] text-white text-sm font-bold px-4 py-2.5 rounded-2xl shadow-xl" data-testid="contact-hero-bubble">
              We&apos;re Here to Help You!
            </div>
          </div>
        </div>
      </section>

      {/* ============ QUICK CONTACT CARDS ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-6" data-testid="contact-quick-cards">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <a href="tel:+919015887887" className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#FFC72C] hover:shadow-[0_14px_30px_-10px_rgba(255,199,44,.45)] transition-all" data-testid="contact-card-call">
            <span className="w-11 h-11 rounded-xl bg-[#FFC72C]/15 text-amber-600 flex items-center justify-center"><Phone className="w-5 h-5" /></span>
            <p className="mt-3.5 font-extrabold text-[15px] text-[#17263B]">Call Us</p>
            <p className="text-xs text-slate-500 mt-1">Mon–Sat, 9AM–8PM</p>
            <p className="text-sm font-bold text-amber-700 mt-1.5">+91 9015 887 887</p>
          </a>
          <a href="mailto:enquiry@trainingbasket.in" className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#FFC72C] hover:shadow-[0_14px_30px_-10px_rgba(255,199,44,.45)] transition-all" data-testid="contact-card-email">
            <span className="w-11 h-11 rounded-xl bg-[#FFC72C]/15 text-amber-600 flex items-center justify-center"><Mail className="w-5 h-5" /></span>
            <p className="mt-3.5 font-extrabold text-[15px] text-[#17263B]">Email Us</p>
            <p className="text-xs text-slate-500 mt-1">We reply within 24 hours</p>
            <p className="text-sm font-bold text-amber-700 mt-1.5 break-all">enquiry@trainingbasket.in</p>
          </a>
          <button type="button" onClick={openChat} className="text-left rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#FFC72C] hover:shadow-[0_14px_30px_-10px_rgba(255,199,44,.45)] transition-all" data-testid="contact-card-chat">
            <span className="w-11 h-11 rounded-xl bg-[#FFC72C]/15 text-amber-600 flex items-center justify-center"><MessageCircle className="w-5 h-5" /></span>
            <p className="mt-3.5 font-extrabold text-[15px] text-[#17263B]">Live Chat</p>
            <p className="text-xs text-slate-500 mt-1">Available on website</p>
            <p className="text-sm font-bold text-amber-700 mt-1.5">Chat with our team</p>
          </button>
          <a href="#visit-centres" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#FFC72C] hover:shadow-[0_14px_30px_-10px_rgba(255,199,44,.45)] transition-all" data-testid="contact-card-visit">
            <span className="w-11 h-11 rounded-xl bg-[#FFC72C]/15 text-amber-600 flex items-center justify-center"><MapPin className="w-5 h-5" /></span>
            <p className="mt-3.5 font-extrabold text-[15px] text-[#17263B]">Visit Us</p>
            <p className="text-xs text-slate-500 mt-1">Our training centres</p>
            <p className="text-sm font-bold text-amber-700 mt-1.5">{offices.map((o) => o.name.replace(/^.*—\s*/, "")).join(" & ")}</p>
          </a>
        </div>
      </section>

      {/* ============ MAP + FORM ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
        <div className="min-w-0" data-testid="contact-map-section">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />
            <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-500">Our Locations</span>
          </div>
          <h2 className="m-0 mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#17263B]">{mapTitle}</h2>
          {mapSub && <p className="mt-2.5 text-sm leading-relaxed text-slate-500">{mapSub}</p>}
          <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Office locations">
            {offices.map((o, i) => (
              <button key={o.tab} role="tab" aria-selected={office === i} onClick={() => setOffice(i)}
                className={`px-4 py-2 rounded-full text-[13px] font-extrabold border transition-all ${office === i ? "bg-[#17263B] text-white border-[#17263B]" : "bg-white text-slate-600 border-slate-200 hover:border-[#FFC72C]"}`}
                data-testid={`contact-map-tab-${i}`}>
                {o.tab}
              </button>
            ))}
          </div>
          {legacySectors.length > 0 && (
            <p className="mt-2 text-xs text-slate-400 font-medium" data-testid="contact-map-legacy">{legacySectors.join(" · ")}</p>
          )}
          <div className="mt-4 rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_45px_-20px_rgba(23,38,59,0.25)]">
            <iframe key={cur.q} title={`Map — ${cur.name}`} src={`https://www.google.com/maps?q=${encodeURIComponent(cur.q)}&output=embed`}
              loading="lazy" className="w-full h-[300px] sm:h-[340px] border-0" data-testid="contact-map-frame" />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cur.q)}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] text-sm font-extrabold transition-colors" data-testid="contact-directions">
              <Navigation className="w-4 h-4" /> Get Directions (Google Maps)
            </a>
            {cur.metro && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cur.metro)}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border-[1.5px] border-slate-200 hover:border-[#FFC72C] text-[#17263B] text-sm font-bold transition-colors" data-testid="contact-metro">
                <Train className="w-4 h-4 text-amber-600" /> Find Nearest Metro
              </a>
            )}
          </div>
        </div>

        <div id="contact" className="min-w-0 scroll-mt-24" data-testid="contact-form-section">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_24px_50px_-24px_rgba(23,38,59,0.25)]">
            <h2 className="m-0 text-2xl sm:text-[1.7rem] font-extrabold tracking-tight text-[#17263B]">{advisorsTitle}</h2>
            {advisorsSub && <p className="mt-2 text-sm text-slate-500">{advisorsSub}</p>}
            {done ? (
              <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="contact-form-success">
                <p className="font-extrabold text-emerald-800 text-lg">Thank you! Your enquiry has been received.</p>
                <p className="text-sm text-emerald-700 mt-1.5">Our career advisors will get back to you shortly. For anything urgent, call +91 9015 887 887.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 flex flex-col gap-4" data-testid="contact-form">
                <div>
                  <label className={lbl} htmlFor="contact-name">Full Name</label>
                  <input id="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter your full name" required className={fld} data-testid="contact-form-name" />
                </div>
                <div>
                  <label className={lbl} htmlFor="contact-phone">Phone Number</label>
                  <PhoneInput id="contact-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="98765 43210" required inputCls={fld} testid="contact-form-phone" />
                </div>
                <div>
                  <label className={lbl} htmlFor="contact-email">Email Address</label>
                  <input id="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className={fld} data-testid="contact-form-email" />
                </div>
                <div>
                  <label className={lbl} htmlFor="contact-course">Course You Are Interested In</label>
                  <select id="contact-course" value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} className={fld} data-testid="contact-form-course">
                    <option value="">Select a course (optional)</option>
                    {programs.map((p) => <option key={p.slug} value={p.slug}>{(p.title || p.slug).split("|")[0].trim()}</option>)}
                  </select>
                </div>
                <div>
                  <span className={lbl}>Preferred Mode of Training</span>
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Preferred mode of training">
                    {["Online", "Offline", "Hybrid"].map((m) => (
                      <button type="button" key={m} onClick={() => setForm({ ...form, mode: m })} aria-pressed={form.mode === m}
                        className={`px-4 py-2 rounded-full text-[13px] font-extrabold border transition-all ${form.mode === m ? "bg-[#FFC72C] border-[#FFC72C] text-[#17263B]" : "bg-white border-slate-200 text-slate-600 hover:border-[#FFC72C]"}`}
                        data-testid={`contact-form-mode-${m.toLowerCase()}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={lbl} htmlFor="contact-message">Your Message</label>
                  <textarea id="contact-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your learning goals…" className={fld + " resize-y"} data-testid="contact-form-message" />
                </div>
                {err && <p className="text-sm font-semibold text-rose-600" data-testid="contact-form-error">{err}</p>}
                <ConsentCheckbox checked={consent} onChange={setConsent} testid="contact-consent" className="mb-3" />
                <button type="submit" disabled={sending}
                  className="mt-1 inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.99] text-[#17263B] font-extrabold text-[15px] transition-all disabled:opacity-60"
                  data-testid="contact-form-submit">
                  <Send className="w-4 h-4" /> {sending ? "Submitting…" : "Submit Enquiry"}
                </button>
              </form>
            )}
            {helplineBits.length > 0 && (
              <a href="tel:+919015887887" className="mt-5 flex items-center gap-3 rounded-2xl bg-[#17263B] text-white px-5 py-4 hover:bg-[#0f1a30] transition-colors" data-testid="contact-helpline">
                <span className="w-10 h-10 rounded-full bg-[#FFC72C] text-[#17263B] flex items-center justify-center shrink-0"><Phone className="w-4.5 h-4.5" /></span>
                <span>
                  <span className="block text-xs text-slate-300 font-semibold">{helplineBits[0]}</span>
                  {helplineBits[1] && <span className="block text-base font-extrabold">{helplineBits[1]}</span>}
                </span>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ============ VISIT OUR CENTRES ============ */}
      <section id="visit-centres" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 scroll-mt-24" data-testid="contact-centres">
        <div className="flex items-center gap-2.5 justify-center">
          <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />
          <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-500">Our Offices</span>
          <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />
        </div>
        <h2 className="m-0 mt-3 text-2xl sm:text-4xl font-extrabold tracking-tight text-[#17263B] text-center">Visit Our Centres</h2>
        <div className="mt-9 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {offices.map((o, i) => (
            <div key={o.tab} className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-[0_18px_40px_-20px_rgba(23,38,59,0.2)]" data-testid={`contact-centre-${i}`}>
              <img src={o.photo} alt={`${o.name} office building`} loading="lazy" className="w-full h-44 object-cover" />
              <div className="p-5">
                <p className="font-extrabold text-[15px] text-[#17263B] flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-600 shrink-0" />{o.name}</p>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-500">{o.address}</p>
                <div className="mt-3 flex flex-col gap-1.5 text-[13px] font-semibold text-slate-600">
                  <span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-amber-600" /> +91 9015 887 887</span>
                  <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-amber-600" /> enquiry@trainingbasket.in</span>
                  <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-amber-600" /> Mon–Sat: 9:00 AM – 8:00 PM</span>
                </div>
                <button onClick={() => { setOffice(i); document.querySelector("[data-testid='contact-map-section']")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-[1.5px] border-[#FFC72C] text-[#17263B] text-sm font-extrabold hover:bg-[#FFC72C] transition-colors"
                  data-testid={`contact-centre-map-${i}`}>
                  View on Map
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ TEAM CAROUSEL ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16" data-testid="contact-team">
        <div className="flex items-center gap-2.5 justify-center">
          <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />
          <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-500">Our Team</span>
          <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />
        </div>
        <h2 className="m-0 mt-3 text-2xl sm:text-4xl font-extrabold tracking-tight text-[#17263B] text-center">Meet Our Friendly Team</h2>
        <div className="relative mt-9 max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-[0_20px_45px_-20px_rgba(23,38,59,0.2)]">
            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${slide * 100}%)` }}>
              {TEAM_PHOTOS.map((t, i) => (
                <img key={i} src={t.src} alt={t.alt} loading="lazy" className="w-full shrink-0 aspect-[16/8] object-cover" data-testid={`contact-team-slide-${i}`} />
              ))}
            </div>
          </div>
          <button onClick={() => setSlide((s) => (s - 1 + TEAM_PHOTOS.length) % TEAM_PHOTOS.length)} aria-label="Previous team photo"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center text-[#17263B] hover:bg-[#FFC72C] transition-colors" data-testid="contact-team-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => setSlide((s) => (s + 1) % TEAM_PHOTOS.length)} aria-label="Next team photo"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center text-[#17263B] hover:bg-[#FFC72C] transition-colors" data-testid="contact-team-next">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="flex justify-center gap-1.5 mt-4">
            {TEAM_PHOTOS.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} aria-label={`Go to team photo ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${slide === i ? "w-6 bg-[#FFC72C]" : "w-1.5 bg-slate-300"}`} data-testid={`contact-team-dot-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ QUICK HELP ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20" data-testid="contact-quick-help">
        <div className="max-w-4xl mx-auto rounded-3xl bg-[#17263B] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#FFC72C]/15 rounded-full blur-2xl" aria-hidden="true" />
          <div className="relative min-w-0">
            <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#FFC72C]">Quick Help</span>
            <h2 className="m-0 mt-2 text-xl sm:text-2xl font-extrabold tracking-tight">Have a Quick Question?</h2>
            <p className="mt-2 text-sm text-slate-300 max-w-md">Browse our frequently asked questions for instant answers about courses, batches, fees, certificates and more.</p>
          </div>
          <div className="relative flex flex-wrap gap-3 shrink-0">
            <a href="https://help.trainingbasket.in/portal/en/home" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] text-sm font-extrabold transition-colors" data-testid="contact-faq-link">
              View FAQs <ArrowRight className="w-4 h-4" />
            </a>
            <button onClick={openChat}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/25 hover:bg-white/20 text-white text-sm font-bold transition-colors" data-testid="contact-help-chat">
              <MessageCircle className="w-4 h-4" /> Chat Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
